import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, LogOut, Search, Bell, Sun, Moon,
  ChevronDown, HelpCircle, Settings, Command,
  ArrowUpRight, Camera, Loader2, Menu
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Navbar = ({ user, onLogout, onProfileClick, searchInput, setSearchInput, isDarkMode, setIsDarkMode, onMenuClick }) => {
  const { updateUser } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('photo', file);

    setIsUploading(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/photo`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ profile_picture: response.data.profile_picture });
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 right-0 left-0 lg:left-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800/80 h-16 sm:h-20 z-40 px-4 sm:px-8 flex items-center justify-between shadow-[0_1px_3px_rgba(0,0,0,0.02)] transition-all duration-300">
      {/* Hamburger for mobile */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 text-gray-500 hover:text-[#D0021B] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all mr-2"
        aria-label="Toggle sidebar"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Brand Section */}
      <div className="flex items-center gap-4 group cursor-pointer">
        <div className="flex flex-col">
          <h1 className="font-serif text-base sm:text-xl font-bold text-[#D0021B] dark:text-[#FF4D4D] tracking-tight transition-colors">
            PRESS ID MANAGEMENT
          </h1>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] font-bold -mt-0.5 hidden sm:block">
            Admin Portal
          </p>
        </div>
      </div>

      {/* Center Section: Integrated Search */}
      <div className="flex-1 max-w-sm mx-8 ml-auto hidden md:block">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400 group-focus-within:text-[#D0021B] transition-colors" />
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="block w-full pl-11 pr-16 py-2.5 bg-gray-200 dark:bg-slate-800/50 border border-transparent dark:border-gray-700/30 rounded-2xl text-sm placeholder-gray-400 dark:text-gray-100 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-red-500/5 focus:border-red-500/20 focus:outline-none transition-all duration-300"
            placeholder="Search records, designations..."
          />

        </div>
      </div>

      {/* Right Section: Tools & Account */}
      <div className="flex items-center gap-5">

        {/* Utility Group */}
        <div className="flex items-center gap-2 px-2 border-r border-gray-100 dark:border-gray-800 mr-2">
          



          {/* Theme Toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-2.5 text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded-xl transition-all"
            title="Toggle Theme"
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>

        {/* Premium Profile Pill */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className={`flex items-center gap-3 pl-1.5 pr-3 py-1.5 rounded-full transition-all duration-300 border ${isProfileOpen
                ? 'bg-white dark:bg-slate-800 border-red-500/20 shadow-md transform scale-105'
                : 'bg-gray-50/50 dark:bg-slate-800/50 border-transparent hover:bg-white dark:hover:bg-slate-800 hover:border-gray-200 dark:hover:border-gray-700'
              }`}
          >
            <div className="relative group/avatar">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                className="hidden"
                accept="image/*"
              />
              <div className="w-9 h-9 rounded-full overflow-hidden shadow-sm border border-white dark:border-gray-700 relative">
                {user?.profile_picture ? (
                  <img
                    src={`${API_URL}${user.profile_picture}`}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-tr from-[#D0021B] to-[#FF4D4D] flex items-center justify-center text-white font-bold text-sm">
                    {(user?.full_name || 'A').charAt(0)}
                  </div>
                )}

                {/* Hover Overlay for Change Photo */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  disabled={isUploading}
                  className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-200"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 text-white" />
                  )}
                </button>
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full z-10"></span>
            </div>

            <div className="text-left hidden lg:block pr-1">
              <p className="text-sm font-bold text-[#D0021B] dark:text-gray-100 leading-tight">
                {user?.full_name?.split(' ')[0] || 'Admin'}
              </p>

            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {isProfileOpen && (
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.95 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="absolute right-0 mt-3 w-64 bg-white dark:bg-slate-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.12)] z-50 overflow-hidden py-2"
              >
                <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-800 mb-1">
                  <p className="text-xs font-bold text-gray-900 dark:text-white truncate uppercase tracking-tight">{user?.full_name || 'Admin User'}</p>
                  <p className="text-[10px] text-gray-400 truncate font-medium">{user?.email || ''}</p>
                </div>

                <div className="px-2 py-1">
                  <button
                    onClick={() => {
                      onProfileClick();
                      setIsProfileOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl group transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400 group-hover:text-[#D0021B]" />
                      <span className="font-medium">My Profile</span>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition-opacity" />
                  </button>

                  <Link
                    to="/support"
                    onClick={() => setIsProfileOpen(false)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl group transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <HelpCircle className="w-4 h-4 text-gray-400 group-hover:text-[#D0021B]" />
                      <span className="font-medium">Help & Support</span>
                    </div>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition-opacity" />
                  </Link>

                  <div className="h-[1px] bg-gray-50 dark:bg-gray-800 my-1.5 mx-3"></div>

                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-all"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-bold uppercase tracking-wider text-[11px]">Logout</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

